/* =====================================================================
 * Fotoposter Editor — Frontend Logic
 * =====================================================================
 * Handles:
 *  - Tab-Switching (Foto / Text / Format)
 *  - Photo-Upload (File API + S3 presigned URL via Django backend)
 *  - Crop-Modal via Cropper.js
 *  - Live-Preview (Foto + Text + Passepartout)
 *  - Font-Picker
 *  - Orientation Toggle
 *  - Passepartout-Swatches
 *  - Frame-Outline Toggle
 *  - Type/Size/Frame Picker (wiederverwendet Starmap-Pattern)
 *  - Cart-Add mit Line-Item-Properties
 *
 * Alle globalen Handlers sind nach `window.generatePhotoAndAddToCart`
 * benannt um Namenskollisionen mit Starmap/Citymap zu vermeiden.
 * ===================================================================== */

(function () {
  'use strict';

  const BACKEND_API_URL = (window.PHOTO_BACKEND_API_URL || 'https://tiles.distantlines.de').replace(/\/+$/, '');

  function generateJobId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // RFC4122 v4 fallback fuer aeltere Browser
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  const MAX_FILE_SIZE_MB = 20;
  const ACCEPT_MIME = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/jpg'];

  // Papier-Groesse -> Portrait aspect ratio (height / width).
  // Poster-Dimensionen in cm — die Preview skaliert PHYSIKALISCH mit
  // der cm-Groesse: 30x40 ist kleiner als 70x100, genau wie bei der
  // Sternkarte. Sowohl Breite als auch Hoehe werden proportional.
  const POSTER_DIMENSIONS = {
    '30x40cm':  { w: 30, h: 40  },
    '40x50cm':  { w: 40, h: 50  },
    '40x60cm':  { w: 40, h: 60  },
    '50x70cm':  { w: 50, h: 70  },
    '60x80cm':  { w: 60, h: 80  },
    '70x100cm': { w: 70, h: 100 },
    '75x100cm': { w: 75, h: 100 },
    // Landscape-Varianten
    '40x30cm':  { w: 40,  h: 30 },
    '50x40cm':  { w: 50,  h: 40 },
    '60x40cm':  { w: 60,  h: 40 },
    '70x50cm':  { w: 70,  h: 50 },
    '80x60cm':  { w: 80,  h: 60 },
    '100x70cm': { w: 100, h: 70 },
    '100x75cm': { w: 100, h: 75 },
  };

  // Groesstes Poster im Sortiment = 100% Preview-Box.
  // Alle kleineren Groessen skalieren proportional runter.
  const MAX_POSTER_LONG_EDGE_CM = 100;
  const DEFAULT_POSTER_DIM = { w: 30, h: 40 };

  function parsePosterDim(sizeStr) {
    if (!sizeStr) return DEFAULT_POSTER_DIM;
    if (POSTER_DIMENSIONS[sizeStr]) return POSTER_DIMENSIONS[sizeStr];
    // Fallback: aus "WxHcm" selbst parsen, falls exotische Groesse.
    const m = String(sizeStr).match(/^(\d+)x(\d+)/i);
    if (m) {
      const w = parseFloat(m[1]);
      const h = parseFloat(m[2]);
      if (w > 0 && h > 0) return { w: w, h: h };
    }
    return DEFAULT_POSTER_DIM;
  }

  // -------------------------------------------------------------------
  // Global State (scoped to photo-customizer)
  // -------------------------------------------------------------------
  const state = {
    user_image_url: null,          // S3 URL des Original-Uploads
    cropped_data_url: null,        // Data URL des zugeschnittenen Bildes (fuer Preview)
    cropped_thumb_url: null,       // Kleine 200px-Variante fuer Filter-Picker-Thumbnails
                                    // (iOS Safari Decode-Block bei 7x voller DataURL — siehe
                                    // Diagnose 2026-04-28).
    filter_thumb_cache: null,      // Pre-rendered: 7 Mini-Variants mit eingebrannten
                                    // Filter-Effekten. {none: dataurl, cool: dataurl, ...}
                                    // Statische Bilder ohne CSS-filter → kein GPU-Compositing
                                    // pro Tap → instant Active-Border-Wechsel auf iOS Safari.
    crop_x: 0,
    crop_y: 0,
    crop_width: 1,
    crop_height: 1,
    crop_rotation: 0,
    title_large: '',
    title_small: '',
    font_family: 'Playfair Display',
    orientation: 'portrait',
    passepartout: 'white',
    frame: 'ohne Rahmen',
    product_type: 'Mattes Poster',
    paper_size: '30x40cm',
    frame_color: 'black',
    // Foto-Filter (Set 1, Desenio-Stil): 'none' | 'cool' | 'bw' | 'soft' |
    // 'sepia' | 'sommer' | 'vintage'. Wird sowohl als CSS-Klasse auf das
    // Foto-Element appliziert als auch als Cart-Property an den Server
    // weitergegeben (Server-CSS hat dieselben Filter-Klassen).
    filter: 'none',
    // Original-Bilddimensionen vom hochgeladenen Foto — fuer Qualitaets-Anzeige
    natural_width: null,
    natural_height: null,
  };

  // Erlaubte Filter-Werte — Whitelist gegen Fremdmanipulation am State.
  const VALID_FILTERS = ['none', 'cool', 'bw', 'soft', 'sepia', 'sommer', 'vintage'];

  // Postergroessen die im Qualitaets-Modal gepruefte werden (Portrait).
  // Bei Querformat werden Width/Height vertauscht.
  const QUALITY_CHECK_SIZES = [
    { w: 30,  h: 40  },
    { w: 40,  h: 50  },
    { w: 50,  h: 70  },
    { w: 70,  h: 100 },
  ];

  // DPI-Schwellen — Grossformat-Poster-Druck (grosser Betrachtungsabstand,
  // ~1.5-2m Wand-Abstand verzeiht niedrige DPI). Verifiziert gegen Desenio
  // Side-by-Side: bei 145 DPI noch grün, daher Schwellen gleich gesetzt.
  //   >= 100 DPI → gruen
  //   75-99      → orange
  //   < 75       → rot
  const QUALITY_THRESHOLDS = { green: 100, orange: 75 };

  let cropperInstance = null;
  let pendingFileForCrop = null;
  // Original-Pixel-Dimensionen des hochgeladenen Bildes — gemerkt BEVOR
  // wir fuer Cropper.js downscalen (siehe resizeDataUrlIfTooLarge).
  // Cropper sieht ab ~2400px Long-Side eine downscaled Variante, fuer
  // die DPI-Anzeige muessen wir aber die echten Original-Dimensionen
  // kennen. Wird im handleFile-reader-Pfad gesetzt, im apply-Handler
  // gelesen, beim Cancel/Reset auf null zurueckgesetzt.
  let pendingOriginalDims = null;
  // Initialer Canvas-zu-Natural-Ratio des Cropper-Bildes. Wird im
  // ready-Hook gesetzt und beim Slider-Sync als Referenz benutzt.
  // 100% Slider = initialRatio (Foto fuellt den Container).
  let initialCropperRatio = null;

  // -------------------------------------------------------------------
  // Image Resize Helper
  // -------------------------------------------------------------------
  // Downscale eine DataURL auf max Long-Side (Pixel) — falls bereits
  // kleiner, wird die Original-DataURL unveraendert zurueckgegeben.
  // Liefert immer auch die echten Original-Dimensionen mit, weil die
  // fuer die DPI-Anzeige im apply-Handler gebraucht werden (Cropper
  // sieht nach Resize nur noch die downscaled Variante).
  //
  // Zweck: Cropper.js darf nicht mit 42-MP-Bildern arbeiten — als RGBA
  // ergeben 7999x5332 ~170 MB pro Bitmap, x2-3 fuer Working-Canvas und
  // Pan/Zoom-Transients = Renderer-Crash (Chrome Code 5, OOM). 2400px
  // Long-Side reicht fuer Editor-Vorschau und laesst genug Headroom
  // fuer Cropper-Zoom auf HiDPI-Mobile (~1080 echte px Bildbreite).
  //
  // Server-Print bleibt unberuehrt: configJson sendet user_image_url
  // (S3-Original) + crop_x/y/width/height als 0..1-Ratio. Die Ratios
  // gelten fuer das downscaled Editor-Bild und das Original gleicher-
  // massen, weil aspect-erhaltend. Siehe generatePhotoAndAddToCart().
  function resizeDataUrlIfTooLarge(dataUrl, maxSide) {
    return new Promise(function (resolve, reject) {
      const img = new Image();
      img.onload = function () {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (w <= maxSide && h <= maxSide) {
          resolve({ dataUrl: dataUrl, originalWidth: w, originalHeight: h });
          return;
        }
        const scale = maxSide / Math.max(w, h);
        const sw = Math.round(w * scale);
        const sh = Math.round(h * scale);
        const canvas = document.createElement('canvas');
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, sw, sh);
        // JPEG q=0.9 — visuell verlustfrei fuer Photo-Content,
        // ~10x kleiner als PNG bei vergleichbarer Qualitaet.
        resolve({
          dataUrl: canvas.toDataURL('image/jpeg', 0.9),
          originalWidth: w,
          originalHeight: h,
        });
      };
      img.onerror = function () { reject(new Error('image decode failed')); };
      img.src = dataUrl;
    });
  }

  // -------------------------------------------------------------------
  // DOM-Refs (lazy — nach DOMContentLoaded gesetzt)
  // -------------------------------------------------------------------
  let root, previewFrame, previewPasse, previewPhoto, previewPlaceholder,
      previewTextLarge, previewTextSmall,
      titleLargeInput, titleSmallInput,
      fileInput, uploadStatus, changeRow, nameDisplay,
      cropperBackdrop, cropperImage;

  // -------------------------------------------------------------------
  // Init — nach DOM-Load
  // -------------------------------------------------------------------
  function init() {
    root = document.querySelector('.photo-customizer');
    if (!root) {
      console.warn('[photo] .photo-customizer not found — skipping init');
      return;
    }

    // Refs
    previewFrame = document.getElementById('photo-frame-container');
    previewPasse = document.getElementById('photo-passepartout-el');
    previewPhoto = document.getElementById('photo-preview-img');
    previewPlaceholder = document.getElementById('photo-placeholder');
    previewTextLarge = document.getElementById('photo-text-large-preview');
    previewTextSmall = document.getElementById('photo-text-small-preview');
    titleLargeInput = document.getElementById('photo-title-large-input');
    titleSmallInput = document.getElementById('photo-title-small-input');
    fileInput = document.getElementById('photo-file-input');
    uploadStatus = document.getElementById('photo-upload-status');
    changeRow = document.getElementById('photo-change-row');
    nameDisplay = document.getElementById('photo-name-display');
    cropperBackdrop = document.getElementById('photo-cropper-backdrop');
    cropperImage = document.getElementById('photo-cropper-image');

    // State aus Defaults uebernehmen
    state.title_large = (titleLargeInput && titleLargeInput.value) || '';
    state.title_small = (titleSmallInput && titleSmallInput.value) || '';

    // Click auf .photo-area (im Render-Frame) -> File-Picker direkt oeffnen.
    // Spart dem User Tab-Wechsel zum Foto-Upload-Block. Pattern wie Desenio.
    const photoArea = document.getElementById('photo-area');
    if (photoArea && fileInput) {
      photoArea.addEventListener('click', () => {
        if (!photoArea.classList.contains('has-photo')) {
          fileInput.click();
        }
      });
    }

    // Handlers
    initTabSwitching();
    initTabHelp();
    initUpload();
    initCropperModal();
    initTextInputs();
    initFontPicker();
    initOrientationPicker();
    initPassepartoutSwatches();
    initTypePicker();
    initSizePicker();
    initFramePicker();
    initQualityModal();
    initFilterPicker();
    initWeiterNav();

    // Initial Preview-Sync
    updatePreview();
    updateQualityIndicator();
    /* Filter-Picker ist immer sichtbar, Thumbnails initial vom Mockup. */
    updateFilterPickerVisibility();
    updateFilterThumbnails();

    // ResizeObserver: previewFrame skaliert mit Viewport (vh-basiert) — Schrift
    // muss bei jedem Resize neu berechnet werden. rAF-Throttling damit wir
    // nicht jeden 1px-Tick re-rendern.
    if (typeof ResizeObserver !== 'undefined' && previewFrame) {
      let rafPending = false;
      const ro = new ResizeObserver(function () {
        if (rafPending) return;
        rafPending = true;
        requestAnimationFrame(function () {
          rafPending = false;
          updateTextSizes();
          applyAdaptiveTextScale();
        });
      });
      ro.observe(previewFrame);
    }
  }

  // -------------------------------------------------------------------
  // Tab Switching (.main__switcher ul > li > a[data-tab])
  // -------------------------------------------------------------------
  function initTabSwitching() {
    const tabs = root.querySelectorAll('.main__switcher > ul > li > a[data-tab]');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = tab.dataset.tab;
        // Deaktiviere alle tabs
        tabs.forEach(function (t) { t.classList.remove('current'); });
        tab.classList.add('current');
        // Alle tab-content containers ausblenden, den richtigen einblenden
        ['foto__wrapper', 'text__wrapper', 'format__wrapper'].forEach(function (id) {
          const el = root.querySelector('.' + id);
          if (el) el.style.display = (id === targetId) ? '' : 'none';
        });

        // Mobile: Tab-Bar an den Top scrollen, damit der neue Tab-Inhalt
        // sichtbar ist. Desktop bleibt aussen vor (Switcher ist dort
        // Flex-Sibling vom Canvas, scrollIntoView wuerde den Page-Header
        // wegrucken — gleicher Grund wie im Weiter-Button-Handler unten).
        if (window.matchMedia('(max-width: 991px)').matches) {
          const sw = tab.closest('.main__switcher');
          if (sw) sw.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        /* V9.96.29c: Auto-Switch auf Holzrahmen bei erstem Klick auf
         * Format-Tab — analog Citymap V9.96.8 / Mama V9.96.28c.
         * Initial-State: Mattes Poster (data-id="1") in Foto/Text-Tabs;
         * sobald Format-Tab das erste Mal aktiv wird, switche auf
         * Holzrahmen (data-id="2") da das der Default-Produkt-Choice ist. */
        if (targetId === 'format__wrapper' && !window.__photoFormatTabVisited) {
          window.__photoFormatTabVisited = true;
          const framedTile = root.querySelector('.tab__switcher a[data-id="2"]:not(.current), .type__picker a[data-id="2"]:not(.current)');
          if (framedTile) framedTile.click();
        }
      });
    });
  }

  // -------------------------------------------------------------------
  // Weiter-Button (am Ende von Tab Foto + Tab Text) — wechselt zum
  // naechsten Tab und scrollt auf Mobile zum Top des Switchers,
  // damit der User den Anfang des neuen Tabs sieht. Cart-Button +
  // Preis liegen jetzt INSIDE Tab Format am Ende, deshalb wird auf
  // Tab 1+2 ein expliziter Weg-zum-Cart-Button gebraucht.
  // -------------------------------------------------------------------
  function initWeiterNav() {
    const buttons = root.querySelectorAll('.weiter-btn[data-target-tab]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const targetTab = btn.dataset.targetTab;
        const scope = btn.closest('.photo-customizer') || root;
        const link = scope.querySelector('.main__switcher > ul > li > a[data-tab="' + targetTab + '"]');
        if (link) link.click();
        // Auto-Scroll nur auf Mobile — auf Desktop ist der Switcher
        // ein Flex-Sibling vom Canvas, scrollIntoView wuerde den
        // ganzen Page-Header wegrucken.
        if (window.matchMedia('(max-width: 991px)').matches) {
          const sw = scope.querySelector('.main__switcher');
          if (sw) sw.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  function initTabHelp() {
    const toggles = root.querySelectorAll('.tab-help__toggle');
    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        const content = toggle.closest('.tab-help').nextElementSibling;
        if (content && content.classList.contains('tab-help__content')) {
          content.style.display = (content.style.display === 'none' || !content.style.display) ? 'block' : 'none';
        }
      });
    });
    const closes = root.querySelectorAll('.tab-help__close a');
    closes.forEach(function (close) {
      close.addEventListener('click', function (e) {
        e.preventDefault();
        const content = close.closest('.tab-help__content');
        if (content) content.style.display = 'none';
      });
    });
  }

  // -------------------------------------------------------------------
  // Upload — File API + Drag/Drop
  // -------------------------------------------------------------------
  function initUpload() {
    const zone = document.getElementById('photo-upload-zone');
    if (!zone) return;

    // Klick auf Label triggert file-input (passiert automatisch durch label-for)
    fileInput.addEventListener('change', function (e) {
      const file = e.target.files && e.target.files[0];
      if (file) handleFile(file);
    });

    // Drag-Drop
    ['dragenter', 'dragover'].forEach(function (evt) {
      zone.addEventListener(evt, function (e) {
        e.preventDefault();
        zone.classList.add('drag-over');
      });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      zone.addEventListener(evt, function (e) {
        e.preventDefault();
        zone.classList.remove('drag-over');
      });
    });
    zone.addEventListener('drop', function (e) {
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) handleFile(file);
    });

    // Foto-aendern Button
    const changeBtn = document.getElementById('photo-change-btn');
    if (changeBtn) {
      changeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        fileInput.click();
      });
    }

    // Neu zuschneiden Button (wenn Foto schon hochgeladen)
    const recropBtn = document.getElementById('photo-recrop-btn');
    if (recropBtn) {
      recropBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (state.user_image_url) {
          openCropperWithUrl(state.user_image_url);
        }
      });
    }
  }

  function handleFile(file) {
    // Validierung
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showStatus('is-error', 'Datei zu gross. Maximal ' + MAX_FILE_SIZE_MB + ' MB.');
      return;
    }
    // HEIC wird vom Browser in FileReader nicht angezeigt, muss via
    // heic2any konvertiert werden. In V1 akzeptieren wir den Upload
    // aber zeigen eine Warnung — Backend muss HEIC→JPEG konvertieren.
    const isHeic = /heic|heif/i.test(file.type) || /\.heic$|\.heif$/i.test(file.name);
    if (isHeic) {
      showStatus('is-uploading', 'HEIC wird verarbeitet...');
    }

    if (!isHeic && !ACCEPT_MIME.includes(file.type)) {
      showStatus('is-error', 'Dateityp nicht unterstuetzt. Bitte JPG, PNG oder HEIC.');
      return;
    }

    pendingFileForCrop = file;

    // Neuer File ausgewaehlt → alte URLs clearen, sonst ueberspringt
    // applyBtn den S3-Upload weil state.user_image_url vom vorherigen
    // Foto noch gesetzt ist. Folge: zweites Foto wird in Editor + Cart
    // ignoriert, das alte Foto bleibt aktiv. Bug-Fix: vor dem Cropping
    // alle Foto-State-Variablen resetten.
    state.user_image_url = null;
    state.cropped_data_url = null;
    state.cropped_thumb_url = null;
    state.filter_thumb_cache = null;
    state.natural_width = null;
    state.natural_height = null;

    if (isHeic) {
      // V1: HEIC direkt uploaden ohne Crop-Preview (Browser zeigt's nicht)
      // V2: heic2any laden, konvertieren, dann croppen
      uploadFileDirect(file);
    } else {
      // Mama-Pattern: S3-Upload startet PARALLEL zum Resize/Cropper.
      // state.user_image_url wird gesetzt sobald public URL da ist; Cart-Add
      // prüft das. UI ist davon entkoppelt — Buttons + Quality erscheinen
      // schon beim Cropper-Apply, nicht erst nach S3.
      uploadFileDirect(file);

      // Crop-Modal oeffnen — Mama-Pattern: Instant-Feedback + Progress-Bar
      pendingOriginalDims = null;
      showStatus('is-uploading', 'Foto wird hochgeladen...', 0);
      const reader = new FileReader();
      reader.onprogress = function (ev) {
        if (ev.lengthComputable) {
          var pct = (ev.loaded / ev.total) * 50;
          showStatus('is-uploading', 'Foto wird hochgeladen...', pct);
        }
      };
      reader.onload = function (ev) {
        showStatus('is-uploading', 'Foto wird hochgeladen...', 60);
        // Vor Cropper-Init auf max 2400px Long-Side downscalen — sonst
        // killt Chrome den Renderer bei Pan/Zoom auf grossen Fotos
        // (42 MP = ~170 MB RGBA pro Bitmap). Print ist nicht betroffen,
        // Server nutzt S3-Original (siehe configJson).
        resizeDataUrlIfTooLarge(ev.target.result, 2400).then(function (result) {
          showStatus('is-uploading', 'Foto wird hochgeladen...', 100);
          pendingOriginalDims = { w: result.originalWidth, h: result.originalHeight };
          setTimeout(function () {
            hideStatus();
            openCropperWithUrl(result.dataUrl);
          }, 250);
        }).catch(function (err) {
          // Defensive: falls Image-Decode scheitert (corruptes File),
          // oeffne Cropper mit Original — alter Crash-Pfad, aber wir
          // verlieren keinen Upload.
          console.warn('[photo] resizeDataUrlIfTooLarge failed, fallback to original:', err);
          pendingOriginalDims = null;
          hideStatus();
          openCropperWithUrl(ev.target.result);
        });
      };
      reader.onerror = function () {
        showStatus('is-error', 'Datei konnte nicht gelesen werden.');
      };
      reader.readAsDataURL(file);
    }
  }

  // -------------------------------------------------------------------
  // Cropper Modal
  // -------------------------------------------------------------------
  function initCropperModal() {
    const applyBtn = document.getElementById('photo-cropper-apply');
    const cancelBtn = document.getElementById('photo-cropper-cancel');

    applyBtn.addEventListener('click', function () {
      if (!cropperInstance) return;
      const data = cropperInstance.getData(true);           // Crop in image-Pixeln
      const imgData = cropperInstance.getImageData();       // Natural size
      const natW = imgData.naturalWidth || imgData.width;   // Cropper-internal (evtl. downscaled)
      const natH = imgData.naturalHeight || imgData.height;

      // Original-Pixel-Dimensionen merken — fuer Druckqualitaets-Anzeige.
      // Cropper sieht ggf. eine downscaled Variante (resizeDataUrlIfTooLarge),
      // fuer DPI rechnen wir aber gegen das echte Original. Fallback auf
      // Cropper-Werte falls der Resize-Pfad nicht durchlief (HEIC-Direct-
      // Upload, Decode-Fail, kleine Bilder ohne Downscale).
      state.natural_width = (pendingOriginalDims && pendingOriginalDims.w) || natW;
      state.natural_height = (pendingOriginalDims && pendingOriginalDims.h) || natH;

      state.crop_x = Math.max(0, Math.min(1, data.x / natW));
      state.crop_y = Math.max(0, Math.min(1, data.y / natH));
      state.crop_width = Math.max(0.01, Math.min(1, data.width / natW));
      state.crop_height = Math.max(0.01, Math.min(1, data.height / natH));
      state.crop_rotation = data.rotate || 0;

      // Gecroppte Preview fuer Live-Anzeige in der Mitte. Cropped-Data-URL
      // ist NUR Editor-Display (nicht im Cart-Property, Server-Print nimmt
      // Original-S3-URL + Crop-Koordinaten). Daher koennen wir die
      // natuerliche Crop-Aufloesung behalten und PNG (verlustfrei) nutzen
      // — keine JPEG-Block-Artifacts in feinen Texturen mehr.
      try {
        const canvas = cropperInstance.getCroppedCanvas({
          fillColor: '#ffffff',
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        });
        state.cropped_data_url = canvas.toDataURL('image/png');

        // Zusätzlich eine kleine Thumbnail-Version (max 200px lange
        // Kante, JPEG q=0.85) für die Filter-Picker-Thumbnails. Auf
        // iOS Safari ist Decoding/GPU-Upload von 7x 24-MP-DataURLs
        // der Bottleneck (1-3 sec Tap-Latenz, Diagnose 2026-04-28).
        // Hauptfoto behält die volle PNG-Auflösung — Thumbnails sind
        // sichtbar nur 64x64, kein Qualitätsverlust.
        try {
          const longEdge = Math.max(canvas.width, canvas.height);
          const scale = longEdge > 200 ? (200 / longEdge) : 1;
          const thumb = document.createElement('canvas');
          thumb.width = Math.round(canvas.width * scale);
          thumb.height = Math.round(canvas.height * scale);
          const tctx = thumb.getContext('2d');
          tctx.drawImage(canvas, 0, 0, thumb.width, thumb.height);
          state.cropped_thumb_url = thumb.toDataURL('image/jpeg', 0.85);
        } catch (err) {
          console.warn('[photo] thumb-canvas failed, falling back to full DataURL:', err);
          state.cropped_thumb_url = state.cropped_data_url;
        }

        // Filter-Thumbnail-Cache vorrendern: jedes Thumbnail bekommt
        // sein eigenes pre-gefiltertes Mini-Image als src — kein CSS-
        // filter-Compositing mehr beim Tap (was iOS Safari pro Frame
        // im GPU-Composite-Pass triggern würde, mit 7 Layern Memory-
        // Pressure → spürbare Tap-Latenz auch bei kleinen Thumbs).
        // Pattern wie Desenio: statische Bilder, nur src-Swap beim Klick.
        try { precomputeFilterThumbCache(state.cropped_thumb_url); } catch (err) {
          console.warn('[photo] precomputeFilterThumbCache failed:', err);
        }
      } catch (err) {
        console.warn('[photo] getCroppedCanvas failed:', err);
      }

      closeCropperModal();
      // Mama-Pattern: Buttons + Quality + Filter SOFORT sichtbar machen.
      // S3-Upload läuft schon parallel im Hintergrund (gestartet in handleFile).
      // Status-Banner verschwindet (showPostUploadUI clearts), Buttons "Neu
      // zuschneiden" + "Anderes Foto" erscheinen sofort — ohne auf S3 zu warten.
      showPostUploadUI();
      updatePhotoPreview();
    });

    cancelBtn.addEventListener('click', function () {
      closeCropperModal();
      pendingFileForCrop = null;
    });

    // Drag-aware Backdrop-Close: nur schliessen wenn pointerdown UND
    // pointerup auf dem Backdrop selbst stattfanden. Sonst verschluckt
    // der naive click-Handler ein Drag-Release das beim Bewegen der
    // Crop-Box aus dem Cropper-Wrapper raus und auf den Backdrop kommt
    // — dann feuert click mit target=backdrop und schliesst das Modal
    // mitten in der User-Interaktion. Zusaetzlich blockiert der dann
    // verschluckte pointerup auch Cropper.js' internen Drag-State, die
    // Crop-Box bleibt haengen bis sein document-level Sync-Listener
    // nach 1-2 sec greift. Symptome (User 2026-04-29): Crop-Box "haengt
    // beim Bewegen" + "Modal geht manchmal einfach aus", auf Desktop
    // wie Mobile.
    var backdropDragArmed = false;
    cropperBackdrop.addEventListener('pointerdown', function (e) {
      backdropDragArmed = (e.target === cropperBackdrop);
    });
    cropperBackdrop.addEventListener('pointerup', function (e) {
      var shouldClose = backdropDragArmed && e.target === cropperBackdrop;
      backdropDragArmed = false;
      if (shouldClose) {
        closeCropperModal();
        pendingFileForCrop = null;
      }
    });

    // Zoom-Slider + Plus/Minus-Buttons — einmalig binden, nicht in
    // openCropperWithUrl (sonst akkumulieren sich Listener bei jedem Foto).
    const zoomSlider = document.getElementById('photo-cropper-zoom-slider');
    if (zoomSlider) {
      zoomSlider.addEventListener('input', function () {
        if (!cropperInstance || !initialCropperRatio) return;
        const factor = parseInt(this.value, 10) / 100;     // 0.5 .. 2.0
        cropperInstance.zoomTo(initialCropperRatio * factor);
      });
    }
    const zoomInBtn = document.getElementById('photo-cropper-zoom-in');
    if (zoomInBtn) zoomInBtn.addEventListener('click', function () {
      if (cropperInstance) cropperInstance.zoom(0.1);
    });
    const zoomOutBtn = document.getElementById('photo-cropper-zoom-out');
    if (zoomOutBtn) zoomOutBtn.addEventListener('click', function () {
      if (cropperInstance) cropperInstance.zoom(-0.1);
    });
  }

  function openCropperWithUrl(url) {
    if (typeof Cropper === 'undefined') {
      console.error('[photo] Cropper.js noch nicht geladen');
      showStatus('is-error', 'Bild-Editor wird noch geladen. Bitte kurz warten.');
      return;
    }
    cropperImage.src = url;
    cropperBackdrop.classList.add('visible');

    if (cropperInstance) {
      try { cropperInstance.destroy(); } catch (e) {}
      cropperInstance = null;
    }

    cropperImage.onload = function () {
      // Crop-Aspect-Ratio MUSS exakt das Aspect der photo-area treffen,
      // sonst zeigen Editor und Server unterschiedlich viel vom Foto:
      // Editor (object-fit:cover) clippt aus dem gecroppten Bild,
      // Server (cover-positioning) zeigt mehr Original drumherum.
      //
      // Wir MESSEN das Aspect direkt aus dem DOM (#photo-area
      // getBoundingClientRect), statt es zu berechnen. Begruendung:
      //  - CSS-Padding-Quirk: Prozent-Padding wird auf BEIDEN Achsen aus
      //    der WIDTH des Containers aufgeloest (CSS-Standard). Eine
      //    Math-Formel wie frameAspect * (88/76) stimmt damit nur fuer
      //    quadratische Frames. Querformat (40x30) und Hochformat (30x40)
      //    liefern systematisch falsche Crop-Aspects → Editor/Print-
      //    Mismatch (User-Bericht 2026-04-28: Mutter+Kind im Crop-Modal
      //    komplett drin, im Querformat-Editor oben+unten geclippt).
      //  - Das echte Aspect haengt zusaetzlich vom passepartout-Modus
      //    ('none' vs Desenio-Layout) und Mobile-@media-Overrides ab.
      //  - Server tut dasselbe (clientWidth/clientHeight in
      //    print-mode.js positionPhoto), wir spiegeln also exakt.
      // Robust gegen alle CSS-/Layout-Effekte, einzig richtige Quelle.
      const photoAreaEl = document.getElementById('photo-area');
      const rect = photoAreaEl ? photoAreaEl.getBoundingClientRect() : null;
      const cropAspect = (rect && rect.height > 0)
        ? (rect.width / rect.height)
        : 1; // Fallback falls photo-area noch nicht gerendert
      if (!rect || rect.height <= 0) {
        console.warn('[photo] photo-area nicht messbar, Fallback aspectRatio: 1');
      }

      // Cropper.js-Init via requestAnimationFrame deferren — der Modal-
      // Display-Switch (cropperBackdrop.classList.add('visible')) ist
      // syncron, aber der Browser-Layout-Reflow kommt erst beim naechsten
      // Frame. Wenn Cropper.js davor initialisiert, nimmt es die alten
      // Container-Maße (display:none → 0x0) und positioniert das Foto
      // falsch. Auf Mobile besonders sichtbar (User-Bericht 2026-04-28:
      // Foto klebt rechts am Cropper-Wrapper, links graue Säule).
      requestAnimationFrame(function () {
      cropperInstance = new Cropper(cropperImage, {
        aspectRatio: cropAspect,
        viewMode: 1,
        autoCropArea: 1,
        movable: true,
        zoomable: true,
        rotatable: false,
        scalable: false,
        cropBoxResizable: true,
        // dragMode:'none' — der Cropper-Default 'crop' zieht beim Ziehen
        // AUSSERHALB der Crop-Box eine neue Box auf. Zwei Clarity-Nutzer
        // haben sich damit die Box winzig gemacht und das Foto "verloren"
        // (10.08.). Groesse aendern geht nur noch ueber die Eck-Griffe;
        // Doppelklick darf den Modus nicht zurueckschalten.
        dragMode: 'none',
        toggleDragModeOnDblclick: false,
        minCropBoxWidth: 60,
        minCropBoxHeight: 60,
        // responsive:false — auf iOS Safari triggert Address-Bar-Show/
        // Hide ein window-resize, Cropper macht dann mitten im Drag
        // einen internen .resize() -> Crop-Box "haengt" bis Sync greift
        // (User-Report 2026-04-29 Desktop+Mobile). Modal-Maße sind eh
        // fix, brauchen kein Responsive-Recalc.
        responsive: false,
        // background:false — Cropper rendert sonst ein 8x8-Schachbrett-
        // Pattern auf der ganzen Cropper-Surface bei jedem Layout-
        // Change. Auf grossen Source-Images (24-MP-Smartphone-Fotos
        // oder DSLR) ist das ein teurer Repaint pro Drag-Frame und
        // verstaerkt zusammen mit responsive:true das Hänger-Symptom.
        background: false,
        modal: true,                 // Cropper-Default-Overlay aussserhalb der
                                      // Crop-Box — Klassen-Konflikt kann nicht
                                      // passieren weil unser Container-DIV
                                      // .photo-crop-modal heisst statt .cropper-modal
        wheelZoomRatio: 0.08,        // sanfter Mausrad-Zoom
        ready: function () {
          // Initial-Ratio = wie weit das Foto in den Container reingezoomt
          // wurde. Cropper feuert ready erneut bei responsive-Resize, dann
          // muss initialRatio neu gesetzt werden.
          const cd = cropperInstance.getCanvasData();
          initialCropperRatio = cd.naturalWidth ? (cd.width / cd.naturalWidth) : null;
          const slider = document.getElementById('photo-cropper-zoom-slider');
          if (slider) slider.value = 100;
          // Force-Resize im naechsten Frame: das Modal-Display + Container-
          // Layout-Reflow ist auf Mobile manchmal noch nicht final wenn
          // ready() feuert. Ein expliziter resize() zwingt Cropper.js zur
          // Neu-Vermessung mit den dann definitiv korrekten Container-Maßen.
          // Behebt User-Bericht 2026-04-28: Foto klebt rechts im Crop-
          // Wrapper auf Mobile-Hochformat trotz rAF-deferred init.
          requestAnimationFrame(function () {
            if (cropperInstance && typeof cropperInstance.resize === 'function') {
              try { cropperInstance.resize(); } catch (e) {}
            }
          });
        },
        zoom: function (ev) {
          if (!initialCropperRatio) return;
          // Hard-Cap: 100% (initial) bis 200% (2x). preventDefault stoppt
          // den Zoom wenn der User per Mausrad/Button drueber rauszieht.
          const minRatio = initialCropperRatio;
          const maxRatio = initialCropperRatio * 2;
          if (ev.detail.ratio < minRatio || ev.detail.ratio > maxRatio) {
            ev.preventDefault();
            return;
          }
          const slider = document.getElementById('photo-cropper-zoom-slider');
          if (!slider) return;
          const pct = Math.round((ev.detail.ratio / initialCropperRatio) * 100);
          slider.value = Math.max(100, Math.min(200, pct));
        },
      });
      });
    };
  }

  function closeCropperModal() {
    cropperBackdrop.classList.remove('visible');
    if (cropperInstance) {
      try { cropperInstance.destroy(); } catch (e) {}
      cropperInstance = null;
    }
    initialCropperRatio = null;
  }

  // -------------------------------------------------------------------
  // Post-Crop UI-Update: Buttons + Quality + Filter sofort sichtbar
  // (Mama-Pattern, entkoppelt vom S3-Upload-State)
  // -------------------------------------------------------------------
  function showPostUploadUI() {
    if (uploadStatus) {
      uploadStatus.className = 'photo-upload-status';
      uploadStatus.innerHTML = '';
    }
    updateQualityIndicator();
    updateFilterPickerVisibility();
    updateFilterThumbnails();
    updateUploadZoneVisibility();
    if (changeRow) changeRow.classList.add('visible');
  }

  // -------------------------------------------------------------------
  // S3 Upload via presigned URL
  // -------------------------------------------------------------------
  function uploadFileDirect(file) {
    // HINWEIS: showStatus wird vom Aufrufer gesetzt (handleFile-Reader-Pfad
    // zeigt schon Progress, HEIC-Pfad zeigt "HEIC wird verarbeitet..."). Hier
    // kein zusätzlicher Status-Spam.

    // 1) Presigned URL holen
    fetch(BACKEND_API_URL + '/api/v1/upload/presigned-url/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
      }),
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Presigned URL fehlgeschlagen: HTTP ' + res.status);
      return res.json();
    })
    .then(function (data) {
      // Backend liefert AWS S3 POST-Policy:
      //   { success, uploadUrl, fields: {...}, publicUrl, key }
      // (auch mit snake_case Fallbacks, falls das Schema mal geaendert wird)
      const uploadUrl = data.uploadUrl || data.upload_url || data.presigned_url;
      const publicUrl = data.publicUrl || data.public_url || data.file_url || data.url;
      const fields    = data.fields || null;
      if (!uploadUrl || !publicUrl) {
        throw new Error('Presigned-Response ungueltig');
      }

      // 2) Datei via multipart POST zu S3 (POST-Policy)
      const formData = new FormData();
      if (fields) {
        Object.keys(fields).forEach(function (k) {
          formData.append(k, fields[k]);
        });
      }
      formData.append('file', file);

      return fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      }).then(function (s3Res) {
        if (!s3Res.ok) throw new Error('S3 Upload fehlgeschlagen: HTTP ' + s3Res.status);
        return publicUrl;
      });
    })
    .then(function (publicUrl) {
      state.user_image_url = publicUrl;
      // Bei HEIC-Pfad ist die UI noch nicht geupdated (kein Cropper-Apply).
      // Bei Crop-Pfad ist showPostUploadUI() schon beim Cropper-Apply gelaufen
      // — idempotent, ein zweiter Aufruf schadet nicht.
      showPostUploadUI();
    })
    .catch(function (err) {
      console.error('[photo] Upload-Fehler:', err);
      showStatus('is-error', 'Upload fehlgeschlagen: ' + err.message);
      // State cleanen + Editor-View zuruecksetzen, damit der User erneut
      // hochladen kann. Sonst Sackgasse: cropped_data_url ist nach Crop-
      // Apply gesetzt, dadurch ist die Upload-Dropzone versteckt
      // (updateUploadZoneVisibility), und ohne sie hat der User keinen
      // Drop-Target. Re-Upload waere nur via "Anderes Foto"-Button moeglich,
      // aber der wird erst im Erfolgs-Pfad eingeblendet — bei Fail also gar
      // nicht. Page-Reload waere die einzige Rettung. Diese vier Zeilen
      // entschaerfen das vollstaendig.
      state.cropped_data_url = null;
      state.cropped_thumb_url = null;
      state.filter_thumb_cache = null;
      updatePhotoPreview();
    });
  }

  function showStatus(cls, msg, pct) {
    if (!uploadStatus) return;
    uploadStatus.className = 'photo-upload-status ' + cls;
    if (cls === 'is-uploading' && typeof pct === 'number') {
      var safePct = Math.max(0, Math.min(100, Math.round(pct)));
      uploadStatus.innerHTML =
        '<div>' + msg + ' ' + safePct + '%</div>' +
        '<span class="progress-bar"><span class="progress-bar-fill" style="width:' + safePct + '%"></span></span>';
    } else {
      uploadStatus.textContent = msg;
    }
  }

  function hideStatus() {
    if (!uploadStatus) return;
    uploadStatus.className = 'photo-upload-status';
    uploadStatus.innerHTML = '';
  }

  // -------------------------------------------------------------------
  // Text Inputs
  // -------------------------------------------------------------------
  function initTextInputs() {
    if (titleLargeInput) {
      titleLargeInput.addEventListener('input', function () {
        state.title_large = titleLargeInput.value;
        if (previewTextLarge) previewTextLarge.textContent = state.title_large;
        updateNoTextState();
        applyAdaptiveTextScale();
      });
    }
    if (titleSmallInput) {
      titleSmallInput.addEventListener('input', function () {
        state.title_small = titleSmallInput.value;
        if (previewTextSmall) previewTextSmall.textContent = state.title_small;
        updateNoTextState();
        applyAdaptiveTextScale();
      });
    }
  }

  // -------------------------------------------------------------------
  // Font Picker
  // -------------------------------------------------------------------
  function initFontPicker() {
    const picker = root.querySelector('.font__picker');
    if (!picker) return;
    const links = picker.querySelectorAll('a[data-font]');
    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        links.forEach(function (l) { l.classList.remove('current'); });
        link.classList.add('current');
        state.font_family = link.dataset.font;
        updateFontPreview();
      });
    });
  }

  function updateFontPreview() {
    if (previewFrame) {
      previewFrame.style.setProperty('--text-font', '"' + state.font_family + '"');
      // data-font ermoeglicht font-spezifische CSS-Overrides (z.B. Script-
      // Fonts mit hohen Aszendern bekommen ggf. eine kleinere Font-Size).
      previewFrame.dataset.font = state.font_family;
    }
    // Andere Schriftart = andere Glyph-Breiten — Scale neu berechnen.
    applyAdaptiveTextScale();
  }

  // -------------------------------------------------------------------
  // Orientation Picker (Portrait/Landscape)
  // -------------------------------------------------------------------
  function initOrientationPicker() {
    const buttons = root.querySelectorAll('.orientation-picker li a');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        buttons.forEach(function (b) { b.classList.remove('current'); });
        btn.classList.add('current');
        state.orientation = btn.dataset.orientation;
        updateOrientation();
      });
    });
  }

  function updateOrientation() {
    if (!previewFrame) return;
    if (state.orientation === 'landscape') {
      previewFrame.classList.add('landscape');
    } else {
      previewFrame.classList.remove('landscape');
    }
    // Rahmen-Overlay muss bei Orientation-Wechsel das passende PNG laden
    var activeType = root.querySelector('.tab__switcher a.current, .type__picker a.current');
    if (activeType) updateFramePreview(activeType.dataset.id);
    // Frame-Breite hat sich geaendert — Scale neu berechnen.
    updateTextSizes();
    applyAdaptiveTextScale();
  }

  // -------------------------------------------------------------------
  // Passepartout Swatches
  // -------------------------------------------------------------------
  function initPassepartoutSwatches() {
    const buttons = root.querySelectorAll('.passe-swatches li a');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        buttons.forEach(function (b) { b.classList.remove('current'); });
        btn.classList.add('current');
        state.passepartout = btn.dataset.passe;
        updatePassepartout();
      });
    });
  }

  function updatePassepartout() {
    if (!previewPasse) return;
    previewPasse.className = 'passepartout passe-' + state.passepartout;
    // passe-none hat 0 Padding (Foto fuellt vollen Rahmen) → mehr Breite
    // fuer den Text. Bei den anderen Varianten 6% Padding L/R.
    applyAdaptiveTextScale();
  }

  // Foto fuellt das ganze Frame mit gleichmaessigem Layout-Rand, wenn beide
  // Title-Felder leer sind. Ohne diesen Toggle bleibt der untere 18%-Streifen
  // sichtbar als leere Flaeche. 1:1 Server (siehe photo_frontend/photo.css).
  function updateNoTextState() {
    if (!previewFrame) return;
    const empty = !state.title_large && !state.title_small;
    previewFrame.classList.toggle('no-text', empty);
  }

  // Adaptive Text-Skalierung (Editor) — Pattern 1:1 aus Server-print-mode.js.
  // Wenn der Text breiter wird als der verfuegbare Container, schrumpft die
  // Schriftgroesse proportional in 5%-Schritten bis er passt oder das
  // Pixel-Minimum erreicht ist. Min 12px im Editor (Server: 10px Print —
  // entspricht ~14% Default-Schrift-Verhaeltnis). Triggert bei jedem Title-
  // Input plus bei Font/Orientation/Passepartout-Wechsel weil sich die
  // verfuegbare Breite oder die Glyph-Breiten dann aendern.
  const ADAPTIVE_MIN_PX = 12;
  function applyAdaptiveTextScale() {
    [previewTextLarge, previewTextSmall].forEach(function (el) {
      if (!el || !el.textContent) return;
      // CSS-Default (5cqw-Fallback bzw. JS-injizierte --text-size-*) wiederherstellen, sonst wuerde der Reset-
      // Wert von einem fruheren Schrumpf-Zyklus uebernommen.
      el.style.fontSize = '';
      const parent = el.parentElement;
      if (!parent) return;
      const availableWidth = parent.clientWidth;
      if (availableWidth <= 0) return;
      let currentSize = parseFloat(window.getComputedStyle(el).fontSize);
      let safety = 40;
      while (el.scrollWidth > availableWidth && currentSize > ADAPTIVE_MIN_PX && safety > 0) {
        currentSize = Math.max(ADAPTIVE_MIN_PX, currentSize * 0.95);
        el.style.fontSize = currentSize + 'px';
        safety -= 1;
      }
    });
  }

  // Min-Side-basierte Schriftgroesse — cqmin/vmin sind im Editor unzuverlaessig
  // (container-type wechselt, Fallback faellt auf vh zurueck). Wir setzen die
  // Werte als CSS-Variablen direkt auf den previewFrame, basierend auf der
  // tatsaechlichen kuerzeren Kante. Damit ist Hoch- und Querformat gleich gross.
  function updateTextSizes() {
    if (!previewFrame) return;
    const w = previewFrame.clientWidth;
    const h = previewFrame.clientHeight;
    const minSide = Math.min(w, h);
    if (minSide <= 0) return;
    previewFrame.style.setProperty('--text-size-large', (minSide * 0.05) + 'px');
    previewFrame.style.setProperty('--text-size-small', (minSide * 0.02) + 'px');
    previewFrame.style.setProperty('--text-size-large-parlare', (minSide * 0.035) + 'px');
  }

  // -------------------------------------------------------------------
  // Type Picker (Mattes Poster / Poster im Holzrahmen / Metall Poster)
  // Type-Namen kommen dynamisch aus den Shopify-Varianten (option1),
  // werden im Snippet citymap-type-picker.liquid als data-type="{{ type }}"
  // auf den Link geschrieben und hier nur ausgelesen.
  // -------------------------------------------------------------------
  function initTypePicker() {
    const typeLinks = root.querySelectorAll('.tab__switcher a[data-id], .type__picker a[data-id]');

    // Initial state.product_type aus dem aktuell aktiven Link uebernehmen
    const activeInit = root.querySelector('.tab__switcher a.current, .type__picker a.current') || typeLinks[0];
    if (activeInit && activeInit.dataset.type) {
      state.product_type = activeInit.dataset.type;
    }

    typeLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        typeLinks.forEach(function (l) { l.classList.remove('current'); });
        link.classList.add('current');
        const typeId = link.dataset.id;
        // Alle Format-Wrappers ausblenden, den richtigen einblenden
        root.querySelectorAll('.el__format').forEach(function (f) {
          f.style.display = (f.dataset.id === typeId) ? '' : 'none';
        });
        /* V9.96.29d: editor-benefits-wrapper pro Type toggeln (analog Mama V9.96.28d) */
        root.querySelectorAll('.editor-benefits-wrapper').forEach(function (wrap) {
          wrap.style.display = (wrap.dataset.id === typeId) ? 'block' : 'none';
        });
        // Product-Type direkt aus dem Link lesen (dynamisch aus Shopify)
        if (link.dataset.type) {
          state.product_type = link.dataset.type;
        }
        // Nach Type-Wechsel die Size aus dem neu sichtbaren Format-Block
        // uebernehmen (jeder Type hat seinen eigenen size__picker mit
        // eigenen Varianten). Vorher-gewaehlte Groesse beibehalten, damit
        // User nicht aus Versehen auf 30x40 zurueckfaellt und unbemerkt
        // ein kleineres Produkt kauft (Vertrauensbruch). Nur fallback auf
        // erste verfuegbare Size, wenn die gewaehlte Groesse in dem
        // Material gar nicht existiert.
        const desiredSize = state.paper_size;
        const newFormat = root.querySelector('.el__format[data-id="' + typeId + '"]');
        if (newFormat) {
          // Alle .current im neuen Format wegnehmen, damit wir sauber
          // den Match (oder Fallback) als current markieren.
          newFormat.querySelectorAll('.size__picker a.current').forEach(function (l) {
            l.classList.remove('current');
          });
          let activeSize = newFormat.querySelector(
            '.size__picker a[data-size="' + desiredSize + '"]'
          );
          if (!activeSize) {
            activeSize = newFormat.querySelector('.size__picker a[data-size]');
          }
          if (activeSize) {
            activeSize.classList.add('current');
          }
          if (activeSize && activeSize.dataset.size) {
            state.paper_size = activeSize.dataset.size;
            updatePosterRatio();
            // Variant-Preis fuer den neuen (Type, Size)-Kombi anzeigen.
            // Snippet photo-size-picker.liquid filtert nach option2+option1
            // → data-price stimmt pro Format-Block.
            if (activeSize.dataset.price) {
              updateVariantPrice(activeSize.dataset.price);
            }
            // Variant-Input auch updaten, sonst zeigt Cart die falsche
            // Variante (preis-relevant beim Add-to-Cart).
            if (activeSize.dataset.variantId) {
              const variantInput = document.querySelector('input[name="id"][data-product-variant-id]');
              if (variantInput) {
                variantInput.value = activeSize.dataset.variantId;
                variantInput.dataset.productVariantId = activeSize.dataset.variantId;
              }
            }
          }
        }
        // Rahmen-Vorschau: nur bei Type 2 (Holzrahmen)
        updateFramePreview(typeId);
      });
    });

    // Initial: Rahmen-Vorschau fuer den aktiven Typ setzen
    if (activeInit && activeInit.dataset.id) {
      updateFramePreview(activeInit.dataset.id);
    }
  }

  // Rahmen-Overlay PNGs (auf Shopify CDN)
  var FRAME_IMAGES = {
    portrait: {
      brown: 'https://cdn.shopify.com/s/files/1/0977/3672/0709/files/brown_fr2.png?v=1774861845',
      white: 'https://cdn.shopify.com/s/files/1/0977/3672/0709/files/white_fr.png',
      black: 'https://cdn.shopify.com/s/files/1/0977/3672/0709/files/black_fr.png'
    },
    landscape: {
      brown: 'https://cdn.shopify.com/s/files/1/0977/3672/0709/files/brown_landscape2.png?v=1774861844',
      white: 'https://cdn.shopify.com/s/files/1/0977/3672/0709/files/white_landscape.png?v=1763296140',
      black: 'https://cdn.shopify.com/s/files/1/0977/3672/0709/files/black_landscape_1.png?v=1763296139'
    }
  };

  function updateFramePreview(typeId) {
    if (!previewFrame) return;
    if (typeId === '2') {
      previewFrame.classList.add('has-frame');
      previewFrame.setAttribute('data-frame-color', state.frame_color || 'black');
    } else {
      previewFrame.classList.remove('has-frame');
      previewFrame.removeAttribute('data-frame-color');
    }
  }

  // -------------------------------------------------------------------
  // Size Picker (pro Format-Block)
  // -------------------------------------------------------------------
  function initSizePicker() {
    const sizeLinks = root.querySelectorAll('.size__picker a[data-size]');
    sizeLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        // Nur Links im aktuell sichtbaren Format-Block deselektieren
        const format = link.closest('.el__format');
        if (format) {
          format.querySelectorAll('.size__picker a[data-size]').forEach(function (l) {
            l.classList.remove('current');
          });
        }
        link.classList.add('current');
        state.paper_size = link.dataset.size;
        // Preview-Rahmen auf neue Groesse skalieren (Starmap-Pattern)
        updatePosterRatio();
        // Variant-ID updaten
        const variantId = link.dataset.variantId;
        if (variantId) {
          const variantInput = document.querySelector('input[name="id"][data-product-variant-id]');
          if (variantInput) {
            variantInput.value = variantId;
            variantInput.dataset.productVariantId = variantId;
          }
          updateVariantPrice(link.dataset.price);
        }
      });
    });

    // Initial state.paper_size aus dem aktuell `.current` markierten Link
    // uebernehmen und Preis-Display sofort synchronisieren (Liquid rendert
    // selected_or_first_available_variant, das kann eine andere Size sein).
    const activeInit = root.querySelector('.el__format:not([style*="display:none"]) .size__picker a.current')
                    || root.querySelector('.size__picker a.current')
                    || root.querySelector('.size__picker a[data-size]');
    if (activeInit && activeInit.dataset.size) {
      state.paper_size = activeInit.dataset.size;
      if (activeInit.dataset.price) {
        updateVariantPrice(activeInit.dataset.price);
      }
    }
  }

  // -------------------------------------------------------------------
  // Poster-Ratio Update. Setzt nur --poster-ratio auf .frame-container;
  // CSS haelt die Hoehe konstant und leitet die Breite daraus ab. Der
  // Kunde sieht wie sich die Form (und damit der Foto-Crop) mit der
  // gewaehlten Groesse veraendert — keine physische Skalierung.
  // -------------------------------------------------------------------
  function updatePosterRatio() {
    if (!previewFrame) return;
    const dim = parsePosterDim(state.paper_size);
    const longEdge  = Math.max(dim.w, dim.h);
    const shortEdge = Math.min(dim.w, dim.h);
    const ratio = shortEdge > 0 ? longEdge / shortEdge : 1.333;
    // Orientation kommt primaer aus state.orientation (User-Wahl),
    // nur bei genuinen Querformat-Sizes (w > h) erzwingen.
    const isLandscape = (state.orientation === 'landscape') || (dim.w > dim.h);

    previewFrame.style.setProperty('--poster-ratio', ratio);
    previewFrame.classList.toggle('landscape', isLandscape);
    previewFrame.dataset.posterSize = state.paper_size;

    // Qualitaets-Banner an die neue Groesse anpassen
    updateQualityIndicator();
    // Poster-Ratio aendert die Frame-Breite → Text neu skalieren.
    updateTextSizes();
    applyAdaptiveTextScale();
  }

  // -------------------------------------------------------------------
  // Frame Color Picker (nur sichtbar bei Format 2)
  // -------------------------------------------------------------------
  function initFramePicker() {
    const frameLinks = root.querySelectorAll('.frame-picker a[data-frame-color]');
    frameLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        frameLinks.forEach(function (l) { l.classList.remove('current'); });
        link.classList.add('current');
        state.frame_color = link.dataset.frameColor;
        const frameMap = { brown: 'Natur', white: 'Weiss', black: 'Schwarz' };
        state.frame = 'Holzrahmen ' + (frameMap[state.frame_color] || 'Natur');
        // Rahmen-Overlay mit neuer Farbe aktualisieren
        updateFramePreview('2');
      });
    });
  }

  // -------------------------------------------------------------------
  // Bildqualitaets-Anzeige (Desenio-Pattern)
  //
  // Berechnung effektive Print-DPI:
  //   cropped_pixels = natural_dim * crop_ratio
  //   inches         = cm / 2.54
  //   dpi            = cropped_pixels / inches
  //   final_dpi      = min(dpi_w, dpi_h)   — die schwaechste Achse zaehlt
  //
  // Schwellen siehe QUALITY_THRESHOLDS oben.
  // -------------------------------------------------------------------
  function computeQualityForPaperSize(paperWidthCm, paperHeightCm) {
    if (!state.natural_width || !state.natural_height) return null;
    const cropW = state.crop_width || 1;
    const cropH = state.crop_height || 1;
    const croppedPxW = state.natural_width * cropW;
    const croppedPxH = state.natural_height * cropH;
    const dpiW = croppedPxW / (paperWidthCm / 2.54);
    const dpiH = croppedPxH / (paperHeightCm / 2.54);
    const dpi = Math.min(dpiW, dpiH);
    let level;
    if (dpi >= QUALITY_THRESHOLDS.green)        level = 'green';
    else if (dpi >= QUALITY_THRESHOLDS.orange)  level = 'orange';
    else                                         level = 'red';
    return { level: level, dpi: Math.round(dpi) };
  }

  function getPaperDimsForCurrentSize() {
    const dim = parsePosterDim(state.paper_size);
    if (!dim) return null;
    const isLandscape = (state.orientation === 'landscape') || (dim.w > dim.h);
    const shortEdge = Math.min(dim.w, dim.h);
    const longEdge  = Math.max(dim.w, dim.h);
    return isLandscape
      ? { w: longEdge,  h: shortEdge }
      : { w: shortEdge, h: longEdge };
  }

  // Update Status-Banner (Quality-Button) anhand der aktuell gewaehlten
  // Postergroesse. Banner-Farbe + Text reflektieren die DPI-Bewertung.
  function updateQualityIndicator() {
    const btn = document.getElementById('photo-quality-btn');
    if (!btn) return;
    // Mama-Pattern: Quality-Math braucht nur natural_width/height (lokal nach
    // Cropper-Apply). cropped_data_url als "Foto-bereit"-Indikator, NICHT
    // user_image_url (= S3-URL, wartet 2-10s auf Upload-Round-Trip).
    if (!state.natural_width || !state.cropped_data_url) {
      btn.style.display = 'none';
      return;
    }
    btn.style.display = 'flex';
    const paperDim = getPaperDimsForCurrentSize();
    if (!paperDim) return;
    const result = computeQualityForPaperSize(paperDim.w, paperDim.h);
    if (!result) return;
    btn.setAttribute('data-level', result.level);
    const statusEl = btn.querySelector('.quality-status');
    if (statusEl) {
      const labels = { green: 'Ausgezeichnet', orange: 'Akzeptabel', red: 'Niedrig' };
      statusEl.textContent = labels[result.level] || '';
    }
    // Icon-SVG je nach Level updaten
    const iconEl = btn.querySelector('.quality-icon');
    if (iconEl) {
      let svg;
      if (result.level === 'green') {
        svg = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7.5l3 3 5-6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      } else if (result.level === 'orange') {
        svg = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      } else {
        svg = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      }
      iconEl.innerHTML = svg;
    }
  }

  // Render die Liste aller Postergroessen im Modal mit Status-Icons.
  function renderQualityModalList() {
    const list = document.getElementById('photo-quality-size-list');
    if (!list) return;
    list.innerHTML = '';
    const isLandscape = state.orientation === 'landscape';
    QUALITY_CHECK_SIZES.forEach(function (size) {
      const w = isLandscape ? Math.max(size.w, size.h) : Math.min(size.w, size.h);
      const h = isLandscape ? Math.min(size.w, size.h) : Math.max(size.w, size.h);
      const result = computeQualityForPaperSize(w, h);
      const li = document.createElement('li');
      const icon = document.createElement('span');
      icon.className = 'quality-icon';
      icon.setAttribute('data-level', result.level);
      // SVG-Symbol je nach Level: Haken / Strich / Kreuz
      let svg;
      if (result.level === 'green') {
        svg = '<svg viewBox="0 0 14 14" fill="none"><path d="M3 7.5l3 3 5-6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      } else if (result.level === 'orange') {
        svg = '<svg viewBox="0 0 14 14" fill="none"><path d="M3 7h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      } else {
        svg = '<svg viewBox="0 0 14 14" fill="none"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      }
      icon.innerHTML = svg;
      li.appendChild(icon);
      const label = document.createElement('span');
      label.textContent = w + 'x' + h + ' cm';
      li.appendChild(label);
      list.appendChild(li);
    });
  }

  function initQualityModal() {
    const btn = document.getElementById('photo-quality-btn');
    const backdrop = document.getElementById('photo-quality-modal');
    const closeBtn = document.getElementById('photo-quality-modal-close');
    if (!btn || !backdrop) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      renderQualityModalList();
      backdrop.classList.add('visible');
    });
    if (closeBtn) closeBtn.addEventListener('click', function () {
      backdrop.classList.remove('visible');
    });
    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) backdrop.classList.remove('visible');
    });
  }

  // -------------------------------------------------------------------
  // Foto-Filter (Set 1, Desenio-Stil)
  //
  // Picker erscheint erst nach Foto-Upload (gleiches Hide-Pattern wie
  // photo-quality-btn). Click setzt state.filter + applied CSS-Klasse
  // auf das Haupt-Foto. Thumbnails zeigen das User-Foto je mit eigenem
  // Filter angewendet (CSS-Filter-Klassen sind in photo.css definiert
  // und auf Server-CSS gespiegelt).
  // -------------------------------------------------------------------
  function initFilterPicker() {
    const picker = document.getElementById('photo-filter-picker');
    if (!picker) return;
    const links = picker.querySelectorAll('a[data-filter]');

    // Hilfsfunktion: visuelle Active-Klasse setzen (nur DOM, kein State).
    function setVisualActive(targetLink) {
      links.forEach(function (l) { l.classList.remove('current'); });
      if (targetLink) targetLink.classList.add('current');
    }
    function restoreActiveFromState() {
      links.forEach(function (l) {
        l.classList.toggle('current', l.dataset.filter === (state.filter || 'none'));
      });
    }

    links.forEach(function (link) {
      // Instant-Tap-Feedback: pointerdown setzt SOFORT die Active-Klasse —
      // KEIN preventDefault, KEIN State-Update. So sieht der User
      // unmittelbar dass sein Tap registriert wurde, der eigentliche
      // Filter-Wechsel folgt im click-Handler 30-50ms spaeter.
      // Falls iOS den Touch als Scroll interpretiert, setzt
      // pointercancel die Active-Klasse zurueck auf den realen aktiven
      // Filter — kein falscher visueller State.
      link.addEventListener('pointerdown', function () {
        setVisualActive(link);
      });
      link.addEventListener('pointercancel', restoreActiveFromState);

      link.addEventListener('click', function (e) {
        e.preventDefault();
        const choice = link.dataset.filter;
        if (VALID_FILTERS.indexOf(choice) === -1) return;
        // Active-Klasse ist evtl. schon vom pointerdown gesetzt — egal,
        // setVisualActive ist idempotent.
        setVisualActive(link);
        state.filter = choice;
        applyFilterToPhoto();
      });
    });
    // Initial-State setzen (default 'none' aus state).
    applyFilterToPhoto();
  }

  // Die aktive Filter-Klasse wird auf das Haupt-Vorschau-Foto im Frame
  // gesetzt. Alle vorigen filter-* Klassen werden vorher entfernt.
  function applyFilterToPhoto() {
    const targets = [];
    if (previewPhoto) targets.push(previewPhoto);
    const mockupEl = document.querySelector('.photo-mockup');
    if (mockupEl) targets.push(mockupEl);
    targets.forEach(function (el) {
      VALID_FILTERS.forEach(function (f) { el.classList.remove('filter-' + f); });
      el.classList.add('filter-' + (state.filter || 'none'));
    });
  }

  // Visibility-Toggle: Filter-Picker zeigen/verstecken abhaengig davon
  // ob ein Foto geladen ist (analog photo-quality-btn). Wir pruefen
  // BEIDE Quellen — cropped_data_url (Editor-Vorschau direkt nach Crop)
  // und user_image_url (S3-Upload abgeschlossen). Sobald eine der
  // beiden gesetzt ist, hat der User ein sichtbares Foto und kann
  // Filter waehlen.
  function updateFilterPickerVisibility() {
    /* Filter-Picker ist IMMER sichtbar — Thumbnails zeigen das Mockup-Foto
     * wenn noch kein echtes Foto hochgeladen ist (siehe updateFilterThumbnails). */
    const picker = document.getElementById('photo-filter-picker');
    if (!picker) return;
    picker.style.display = '';
  }

  // Upload-Dropzone (das grosse gepunktete Rechteck "Klicken oder Foto
  // hierher ziehen") wird nach Foto-Upload ausgeblendet — der "Anderes
  // Foto"-Button in der photo-change-row deckt die Re-Upload-Funktion
  // ab. Gleicher Trigger wie updateFilterPickerVisibility (cropped_data_url
  // oder user_image_url gesetzt).
  function updateUploadZoneVisibility() {
    const zone = document.getElementById('photo-upload-zone');
    if (!zone) return;
    const hasPhoto = !!(state.cropped_data_url || state.user_image_url);
    zone.style.display = hasPhoto ? 'none' : '';
    const hint = document.querySelector('.photo-upload-hint');
    if (hint) hint.style.display = hasPhoto ? 'none' : '';
  }

  // Thumbnails mit dem User-Foto updaten.
  //
  // Zwei Modi:
  //   1) state.filter_thumb_cache liegt vor → jedes Thumbnail bekommt
  //      sein eigenes pre-gefiltertes Mini-Image als src. Auf
  //      .filter-options Container wird die Klasse `.filters-baked`
  //      gesetzt — CSS-filter wird dadurch deaktiviert (siehe
  //      photo.css), sonst hätten wir doppel-applizierte Filter.
  //   2) Cache noch nicht ready (~erster Frame nach Crop) → fallback
  //      auf cropped_thumb_url + CSS-filter via filter-* Klassen.
  function updateFilterThumbnails() {
    const picker = document.getElementById('photo-filter-picker');
    if (!picker) return;
    const opts = picker.querySelector('.filter-options');
    const cache = state.filter_thumb_cache;
    /* Mockup-URL als finaler Fallback wenn noch kein echtes Foto hochgeladen —
     * Filter-Picker ist immer sichtbar, Thumbnails zeigen das Mockup damit der
     * User die Filter-Wirkung schon vor Upload sehen kann. */
    const mockupEl = document.querySelector('.photo-mockup');
    const mockupSrc = mockupEl ? mockupEl.getAttribute('src') : null;
    const fallbackSrc = state.cropped_thumb_url || state.cropped_data_url || state.user_image_url || mockupSrc;
    if (opts) opts.classList.toggle('filters-baked', !!cache);
    const thumbs = picker.querySelectorAll('.filter-thumb-img');
    thumbs.forEach(function (thumb) {
      let src = fallbackSrc;
      if (cache) {
        // Filter-Name aus filter-* Klasse extrahieren (filter-bw → 'bw').
        const m = thumb.className.match(/filter-(none|cool|bw|soft|sepia|sommer|vintage)/);
        if (m && cache[m[1]]) src = cache[m[1]];
      }
      if (src) {
        if (thumb.getAttribute('src') !== src) thumb.setAttribute('src', src);
      } else {
        thumb.removeAttribute('src');
      }
    });
  }

  // Pre-rendered Filter-Cache: für jeden der 7 Filter ein Off-Screen-
  // Canvas, ctx.filter applied, output als DataURL. Resultat sind 7
  // statische Mini-Images mit eingebrannten Filtern — der Filter-Picker
  // muss beim Tap nur die src austauschen, kein GPU-Filter-Recompute.
  function precomputeFilterThumbCache(srcDataUrl) {
    if (!srcDataUrl) return;
    const filterCss = {
      none:    'none',
      cool:    'saturate(0.85) hue-rotate(-10deg) brightness(1.05)',
      bw:      'grayscale(1) contrast(1.05)',
      soft:    'saturate(0.6) brightness(1.05) contrast(0.95)',
      sepia:   'sepia(0.85) saturate(1.1) brightness(0.95)',
      sommer:  'saturate(1.25) brightness(1.08) hue-rotate(-5deg)',
      vintage: 'sepia(0.4) saturate(1.4) hue-rotate(-15deg) brightness(0.92)',
    };
    const img = new Image();
    img.onload = function () {
      try {
        const cache = {};
        Object.keys(filterCss).forEach(function (name) {
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          const ctx = c.getContext('2d');
          ctx.filter = filterCss[name];
          ctx.drawImage(img, 0, 0);
          cache[name] = c.toDataURL('image/jpeg', 0.85);
        });
        state.filter_thumb_cache = cache;
        // Sofort die Thumbnails mit den gecachten Versionen befüllen.
        updateFilterThumbnails();
      } catch (err) {
        console.warn('[photo] filter-thumb-cache build failed:', err);
        state.filter_thumb_cache = null;
      }
    };
    img.onerror = function () {
      console.warn('[photo] filter-thumb-cache: source image failed to load');
      state.filter_thumb_cache = null;
    };
    img.src = srcDataUrl;
  }

  // -------------------------------------------------------------------
  // Variant-Price Display
  // -------------------------------------------------------------------
  function updateVariantPrice(priceNum) {
    const priceEl = root.querySelector('.variant-price');
    if (priceEl && priceNum) {
      /* V9.96.29e: Format "XX,XX €" (Symbol HINTER Zahl, analog Citymap V9.96.5 / Mama V9.96.28e) */
      try {
        const eur = (parseInt(priceNum, 10) / 100).toFixed(2).replace('.', ',');
        priceEl.textContent = eur + ' €';
      } catch (e) {}
    }
  }

  // -------------------------------------------------------------------
  // Live-Preview Update
  // -------------------------------------------------------------------
  function updatePreview() {
    updatePhotoPreview();
    updatePassepartout();
    updateOrientation();
    updateFontPreview();
    updatePosterRatio();
    if (previewTextLarge) previewTextLarge.textContent = state.title_large || '';
    if (previewTextSmall) previewTextSmall.textContent = state.title_small || '';
    updateNoTextState();
    updateTextSizes();
    applyAdaptiveTextScale();
    applyFilterToPhoto();
    updateFilterPickerVisibility();
    updateFilterThumbnails();
  }

  function updatePhotoPreview() {
    if (!previewPhoto || !previewPlaceholder) return;

    // Filter-Picker Visibility + Thumbnails synchronisieren — laeuft
    // sowohl bei "Foto da" als auch bei "Foto wieder weg" (Reset/Re-Upload).
    updateFilterPickerVisibility();
    updateFilterThumbnails();
    // Upload-Dropzone nach Foto-Upload ausblenden — gleiche has-photo-Logik.
    updateUploadZoneVisibility();

    // Welches Bild anzeigen? Cropped Preview > Original > keines
    const src = state.cropped_data_url || state.user_image_url;
    const area = previewPhoto.parentElement;

    if (!src) {
      previewPhoto.style.display = 'none';
      previewPlaceholder.style.display = 'flex';
      if (area) area.classList.remove('has-photo');
      return;
    }

    if (area) area.classList.add('has-photo');
    previewPlaceholder.style.display = 'none';
    previewPhoto.style.display = 'block';

    // Alle von aelteren Versionen geschriebenen Inline-Pixelwerte wegwerfen.
    // Size/Position regelt jetzt photo.css via object-fit: cover.
    previewPhoto.style.width = '';
    previewPhoto.style.height = '';
    previewPhoto.style.left = '';
    previewPhoto.style.top = '';

    if (previewPhoto.src !== src) {
      previewPhoto.src = src;
    }
    // Aktive Filter-Klasse erneut auf das Foto anwenden — bei src-Wechsel
    // bleibt sie sonst zwar erhalten (Klassen sind am Element), aber wir
    // ziehen es zur Sicherheit nach.
    applyFilterToPhoto();
  }

  // -------------------------------------------------------------------
  // Cart-Add (public)
  // -------------------------------------------------------------------
  async function generatePhotoAndAddToCart(event) {
    if (event && event.preventDefault) event.preventDefault();

    // Validation: Foto muss hochgeladen sein
    // V9.96.29j: Modal statt alert (analog Mama definitionsposter)
    if (!state.user_image_url) {
      const noticeModal = document.getElementById('cart-no-photo-modal');
      if (noticeModal) {
        noticeModal.classList.add('visible');
      } else {
        alert('Bitte lade zuerst ein Foto hoch.');
      }
      return;
    }

    // Variant-ID frueh validieren — bevor wir den Server bemuehen
    const variantInput = document.querySelector('input[name="id"][data-product-variant-id]');
    const variantId = variantInput ? variantInput.value : null;
    if (!variantId) {
      alert('Bitte waehle zuerst ein Format und eine Groesse.');
      return;
    }

    // Job-ID als UUID — Server speichert das als cart_token,
    // GET /api/v1/photo/data/<uuid:job_id>/ verlangt UUID-Format.
    const jobId = generateJobId();
    const printImageUrl = BACKEND_API_URL + '/api/v1/photo/data/' + jobId + '/';

    // print-Block (Citymap-Pattern citymap.js → config_json.print). Server
    // liest width/height direkt daraus statt aus dem paper_size-Label, damit
    // Querformat-Bestellungen tatsaechlich Querformat gerendert werden.
    // Bei Landscape: lange Seite = width, kurze Seite = height.
    const dim       = parsePosterDim(state.paper_size);
    const longEdge  = Math.max(dim.w, dim.h);
    const shortEdge = Math.min(dim.w, dim.h);
    const isLandscape = state.orientation === 'landscape';

    // Config-JSON zusammenbauen
    const configJson = {
      order_id: jobId,
      user_image_url: state.user_image_url,
      crop_x: state.crop_x,
      crop_y: state.crop_y,
      crop_width: state.crop_width,
      crop_height: state.crop_height,
      crop_rotation: state.crop_rotation,
      title_large: state.title_large,
      title_small: state.title_small,
      font_family: state.font_family,
      orientation: state.orientation,
      passepartout: state.passepartout,
      layout_type: state.product_type,
      frame: state.frame,
      frame_color: state.frame_color,
      product_type: state.product_type,
      paper_size: state.paper_size,
      variant_id: variantId,
      filter: state.filter || 'none',
      print: {
        width:  isLandscape ? longEdge  : shortEdge,
        height: isLandscape ? shortEdge : longEdge,
        dpi: 300,
      },
    };

    const cartBtn = document.querySelector('.cart-btn');
    const cartBtnDefaultText = cartBtn ? cartBtn.textContent : '';
    if (cartBtn) {
      cartBtn.style.pointerEvents = 'none';
      cartBtn.textContent = 'Foto wird verarbeitet...';
    }

    // Schritt 1: Render-Job am Backend anstossen — Pattern wie citymap.js
    try {
      const renderRes = await fetch(BACKEND_API_URL + '/api/v1/photo/render/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(configJson),
      });
      if (!renderRes.ok) {
        let msg = 'Render-Auftrag fehlgeschlagen';
        try {
          const err = await renderRes.json();
          msg = err.error || err.message || msg;
        } catch (_) {
          msg = 'HTTP ' + renderRes.status + ' ' + renderRes.statusText;
        }
        throw new Error(msg);
      }
      // Response wird nicht weiter benoetigt — print URL ist deterministisch
    } catch (err) {
      console.error('[photo] Render-Trigger Fehler:', err);
      alert('Foto konnte nicht verarbeitet werden: ' + err.message + '\n\nBitte versuche es nochmal.');
      if (cartBtn) {
        cartBtn.style.pointerEvents = '';
        cartBtn.textContent = cartBtnDefaultText;
      }
      return;
    }

    // Properties direkt im JS-Object aufbauen — gleiches Pattern wie
    // Citymap (citymap.js:9279) und Starmap (starmap-celestial.js:2540).
    // Konsistent + deterministisch, kein Hidden-Input-Pfad mehr (war
    // fehleranfaellig, _Frame Color landete nicht zuverlaessig im Cart).
    // User-Vorgabe 2026-05-23: max 4 sichtbare Properties im Cart
    // (Title large/small + Product Type + Paper Size). Rest weg —
    // Server-Print nutzt configJson, einzelne Properties sind reine
    // Cart/Order-Anzeige. Hidden-Properties (_prefix) bleiben für
    // Print-Pipeline.
    const properties = {
      '_Photo Config JSON': JSON.stringify(configJson),
      '_User Image URL': state.user_image_url,
      '_Title (Large)': state.title_large,
      '_Title (Small)': state.title_small,
      '_Product Type': state.product_type,
      '_Paper Size': state.paper_size,
      '_print_image_url': printImageUrl,
      '_job_id': jobId,
      // Cart-Vorschau (PROJ-72): persistentes Komposit vom Render-Server —
      // der Warenkorb zeigt es nach Reload / auf anderen Geraeten.
      '_preview_image_url': BACKEND_API_URL + '/media/blanket_renders/' + jobId + '_cartpreview.jpg',
    };

    // _Frame Color nur bei Holzrahmen-Type (gleiche Bedingung wie
    // Citymap citymap.js:9291 / Starmap starmap-celestial.js:2571).
    if (state.frame_color && state.product_type && state.product_type.indexOf('Holzrahmen') !== -1) {
      properties['_Frame Color'] = state.frame_color;
    }

    // Leere Werte filtern — sonst blaehen sie die Bestellung auf.
    Object.keys(properties).forEach(function (k) {
      if (!properties[k]) delete properties[k];
    });

    // Cart-Add
    if (cartBtn) cartBtn.textContent = 'Wird hinzugefuegt...';

    // Sofort-Vorschau fuer den Warenkorb (PROJ-72): Schnappschuss der
    // sichtbaren Poster-Vorschau (frame-container) in sessionStorage —
    // der Cart-Drawer zeigt das Motiv sofort, ohne auf den Server-Render
    // zu warten. Helper ist fail-soft (2,5s-Notbremse), der Kauf wird
    // nie blockiert.
    const vorschauFertig = window.kaSofortVorschau
      ? window.kaSofortVorschau(previewFrame, jobId)
      : Promise.resolve();
    await vorschauFertig;

    try {
      const cartRes = await fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: properties,
          }],
        }),
      });
      if (!cartRes.ok) {
        const text = await cartRes.text();
        throw new Error(text);
      }
      const cartData = await cartRes.json();

      // GTM dataLayer AddToCart Event
      if (window.DLTrack && window.DLTrack.addToCart) {
        window.DLTrack.addToCart(cartData);
      }

      // Cart-Drawer benachrichtigen + oeffnen — Pattern wie starmap-celestial.js
      // Damit kann der User mehrere Fotoposter nacheinander hinzufuegen ohne Page-Reload.
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart: cartData } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else {
        // Kein Drawer im Theme verfuegbar → Fallback auf Cart-Page
        window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart';
        return;
      }

      // Erfolgsfeedback. Editor-State bleibt erhalten (wie Citymap/Starmap):
      // wenn der User den Cart-Drawer schliesst, soll seine Konfiguration
      // weiter sichtbar sein, nicht das Default-Bild ohne Text.
      if (cartBtn) {
        cartBtn.textContent = '✓ Hinzugefuegt';
        cartBtn.style.pointerEvents = '';
        setTimeout(function () {
          if (cartBtn) cartBtn.textContent = cartBtnDefaultText;
        }, 1800);
      }
    } catch (err) {
      console.error('[photo] Cart-Add Fehler:', err);
      alert('Warenkorb-Fehler: ' + err.message);
      if (cartBtn) {
        cartBtn.style.pointerEvents = '';
        cartBtn.textContent = cartBtnDefaultText;
      }
    }
  }

  // -------------------------------------------------------------------
  // Editor-Reset nach erfolgreichem Cart-Add — erlaubt mehrfaches
  // Hinzufuegen ohne Seiten-Reload. File-Input-Value muss explizit
  // geleert werden, sonst akzeptiert der Browser dieselbe Datei nicht
  // wieder (kein change-Event).
  // -------------------------------------------------------------------
  function resetEditorForNextAdd() {
    state.user_image_url = null;
    state.cropped_data_url = null;
    state.cropped_thumb_url = null;
    state.filter_thumb_cache = null;
    state.crop_x = 0;
    state.crop_y = 0;
    state.crop_width = 1;
    state.crop_height = 1;
    state.crop_rotation = 0;
    state.title_large = '';
    state.title_small = '';
    state.natural_width = null;
    state.natural_height = null;
    state.filter = 'none';
    updateQualityIndicator();
    // Filter-Picker zuruecksetzen: Picker verstecken + Active-Reset auf "Original".
    updateFilterPickerVisibility();
    updateFilterThumbnails();
    // Upload-Zone wieder einblenden — Editor ist nach Cart-Add reset.
    updateUploadZoneVisibility();
    const filterPicker = document.getElementById('photo-filter-picker');
    if (filterPicker) {
      filterPicker.querySelectorAll('a[data-filter]').forEach(function (l) {
        l.classList.toggle('current', l.dataset.filter === 'none');
      });
    }
    applyFilterToPhoto();

    if (cropperInstance) {
      try { cropperInstance.destroy(); } catch (e) {}
      cropperInstance = null;
    }
    pendingFileForCrop = null;

    if (fileInput) fileInput.value = '';
    if (previewPhoto) {
      previewPhoto.src = '';
      previewPhoto.style.display = 'none';
    }
    if (previewPlaceholder) previewPlaceholder.style.display = '';
    if (changeRow) changeRow.style.display = 'none';
    if (nameDisplay) nameDisplay.textContent = '';
    if (uploadStatus) uploadStatus.textContent = '';
    if (titleLargeInput) titleLargeInput.value = '';
    if (titleSmallInput) titleSmallInput.value = '';
    if (previewTextLarge) previewTextLarge.textContent = '';
    if (previewTextSmall) previewTextSmall.textContent = '';

    // Cart-Button-Pointer-Events freigeben (der textContent-Restore
    // laeuft schon im setTimeout aus dem Cart-Add-Block).
    const cartBtn = document.querySelector('.cart-btn');
    if (cartBtn) cartBtn.style.pointerEvents = '';
  }

  // -------------------------------------------------------------------
  // BFCache-Fix: wenn der User nach /cart per Browser-Back zurueckkommt,
  // wird die Page evtl. aus dem Back-Forward-Cache wiederhergestellt.
  // File-Inputs sind in dem Zustand "gefroren" und akzeptieren keine
  // neue Auswahl mehr. Reload erzwingen.
  // -------------------------------------------------------------------
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      window.location.reload();
    }
  });

  function setVal(id, value) {
    const el = document.getElementById(id);
    if (el) el.value = value || '';
  }

  // Public export
  window.generatePhotoAndAddToCart = generatePhotoAndAddToCart;

  /* V9.96.29j: Cart-no-photo-modal — Close-Btn + CTA-Button öffnet File-Picker */
  function initNoticeModal() {
    const noticeModal = document.getElementById('cart-no-photo-modal');
    const noticeClose = document.getElementById('cart-no-photo-close');
    const noticeCta = document.getElementById('cart-no-photo-cta');
    if (!noticeModal) return;
    function close() { noticeModal.classList.remove('visible'); }
    if (noticeClose) noticeClose.addEventListener('click', close);
    /* Klick aufs Backdrop (außerhalb der Modal-Box) schließt ebenfalls */
    noticeModal.addEventListener('click', function (e) {
      if (e.target === noticeModal) close();
    });
    if (noticeCta) {
      noticeCta.addEventListener('click', function (e) {
        e.preventDefault();
        close();
        const fileInput = document.getElementById('photo-file-input');
        if (fileInput) fileInput.click();
      });
    }
  }

  // -------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); initNoticeModal(); });
  } else {
    init();
    initNoticeModal();
  }
})();
